import { createAsyncThunk } from '@reduxjs/toolkit';
import constructGiftCardTopUpPaymentRequest from '../utils/constructGiftCardTopUpPaymentRequest';
import { setPaymentFormInUse } from '../slices/paymentFormInUse';
import { RedcatApiHandler } from 'polygon-utils';
import { enqueueErrorSnackbar, enqueueSuccessSnackbar } from '../utils/snackbar';
import { setPaymentMethod } from '../slices/paymentMethod';
import { setPaymentFormFilled } from '../slices/paymentFormFilled';
import { setSavedCardToken } from '../slices/savedCardToken';
import { OrderingConstants, OrderingOperations, OrderingSelectors } from 'polygon-ordering';
import type { RootState } from '../store';
import { store } from '../store';
import { setCurrentModal } from '../thunks/setCurrentModal';
import { setGoogleReCaptcha } from '../slices/googleReCaptcha';
import { GIFTCARD_PURCHASE_SUCCESSFUL_MODAL } from '../modals/GiftcardSuccessModal';
import { purchaseGiftCard } from '../utils/analytics';

const { PAYMENT_METHOD } = OrderingConstants;
const { getEnableDynamicPaymentGatewayConfig, getLocation, getSaleType } = OrderingSelectors;
const { fetchPaymentGatewayConfig, fetchPaymentGatewayTokens, fetchMember } = OrderingOperations;

export const processPayment = createAsyncThunk(
  'processPayment',
  async (data: ProcessPaymentData, { dispatch, getState, rejectWithValue }) => {
    const paymentMethod = (getState() as RootState).paymentMethod;
    const { body, path, modal } = constructGiftCardTopUpPaymentRequest(data);
    const profile = (store.getState() as RootState).profile;
    const enableDynamicPaymentGatewayConfig = getEnableDynamicPaymentGatewayConfig(
      store.getState() as RootState,
    );
    const giftCardTopUpLocationId = (store.getState() as RootState).config.giftCardTopUpLocationId;
    const location = getLocation(store.getState() as RootState);
    const saleType = getSaleType(store.getState() as RootState);

    const fetch = profile ? RedcatApiHandler.authorisedFetch : RedcatApiHandler.fetch;

    return fetch({ method: 'POST', path, body })
      .then((response: { success: any; data: { msg: any; Amount: number } }) => {
        if (response.success) {
          if (enableDynamicPaymentGatewayConfig) {
            dispatch(fetchPaymentGatewayConfig({ locationId: giftCardTopUpLocationId as string }));
          } else {
            dispatch(fetchPaymentGatewayTokens({}));
          }
          if (data.route === 'memberCashTopUp') {
            dispatch(fetchMember({ authenticationMethod: 'member' }));
          }
          if (modal !== '') {
            const isGift = Boolean(data.values.RecipientEmail);
            const purchaserEmail = data.values.Email ?? profile?.emailAddress ?? '';
            const recipientEmail = isGift ? data.values.RecipientEmail! : purchaserEmail;
            const scheduled =
              isGift && Boolean(data.values.delivery) && Boolean(data.values.SendTime);

            dispatch(
              setCurrentModal({
                modalId: modal,
                params: {
                  giftcard: response.data as any,
                  isGift,
                  purchaserEmail,
                  recipientEmail,
                  scheduled,
                  sendTime: data.values.SendTime,
                },
              }),
            );
          } else {
            enqueueSuccessSnackbar(response.data.msg || 'Success');
          }
          dispatch(setPaymentFormInUse(false));
          dispatch(setPaymentMethod(null));
          dispatch(setPaymentFormFilled(false));
          dispatch(setSavedCardToken(null));
          dispatch(setGoogleReCaptcha(null));
          if (modal === GIFTCARD_PURCHASE_SUCCESSFUL_MODAL) {
            purchaseGiftCard({ amount: response.data.Amount, store: location?.name, saleType });
          }
        }

        return response;
      })
      .catch((err: any) => {
        if (
          paymentMethod?.method !== PAYMENT_METHOD.APPLE_PAY &&
          paymentMethod?.method !== PAYMENT_METHOD.GOOGLE_PAY &&
          err.details.json.error_code !== 206
        ) {
          dispatch(setPaymentFormInUse(false));
          dispatch(setGoogleReCaptcha(null));
          enqueueErrorSnackbar(err);
        }

        err = { ...err, modal };

        return rejectWithValue(err);
      });
  },
);

interface _3dsData {
  orderInfo: any;
  actionResponse: {
    payment_intent_id: string;
  };
  modal: string;
}

export const process3ds = createAsyncThunk(
  'process3ds',
  async (data: _3dsData, { dispatch, rejectWithValue }) => {
    const profile = (store.getState() as RootState).profile;

    const { modal } = data;

    const fetch = profile ? RedcatApiHandler.authorisedFetch : RedcatApiHandler.fetch;
    const path = profile ? '/api/v1/sale/finalise' : '/api/v1/sale/finalise/nonmember';

    return fetch({ method: 'POST', path, body: data })
      .then((response: { data: any; success?: any }) => {
        const { data, success } = response;

        if (success) {
          if (modal !== '') {
            dispatch(
              setCurrentModal({
                modalId: modal,
                params: {
                  giftcard: response.data,
                },
              }),
            );
          } else {
            enqueueSuccessSnackbar(data.msg ? data.msg : 'Success');
          }
          dispatch(setPaymentFormInUse(false));
          dispatch(setPaymentMethod(null));
          dispatch(setPaymentFormFilled(false));
          dispatch(setSavedCardToken(null));
        }
      })
      .catch((err: any) => {
        enqueueErrorSnackbar(err);
        dispatch(setPaymentFormInUse(false));
        return rejectWithValue(err);
      });
  },
);
